import { useEffect, useRef, useState, useCallback } from 'react';
import { FileText, X, Play, Download, FileDown, BookOpen, RefreshCw } from 'lucide-react';
import { gerarLatex } from './latex/generators/latex-generator';
import { escapeHtml } from '@/utils/html-escape';
import { apiService } from '@/services/api';
import { useI18n } from '@/contexts/i18n-hook';

type TipoNavegacao = 'primeira' | 'sumario' | 'ultima_parte' | 'ultima';

interface PreviewLatexProps {
  apreciacao: any;
  dados: any;
  riscos: any[];
  onClose?: () => void;
  paginaUltimaParte?: number;
}

export function PreviewLatex({ apreciacao, dados, riscos, onClose, paginaUltimaParte }: PreviewLatexProps) {
  const { t } = useI18n();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const pdfUrlRef = useRef<string | null>(null);
  const pdfBlobRef = useRef<Blob | null>(null);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isFirstRenderRef = useRef(true);
  const apreciacaoIdRef = useRef<string | undefined>(undefined);
  const abortControllerRef = useRef<AbortController | null>(null);
  const [isRendered, setIsRendered] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tipoNavegacao, setTipoNavegacao] = useState<TipoNavegacao>('primeira');

  const getPaginaAlvo = useCallback((): number => {
    switch (tipoNavegacao) {
      case 'primeira': return 1;
      case 'sumario': return 2;
      case 'ultima_parte': return paginaUltimaParte ?? 1;
      case 'ultima': return 9999;
      default: return 1;
    }
  }, [tipoNavegacao, paginaUltimaParte]);

  // Renderizar PDF no backend com suporte a cancelamento
  const renderizarPdf = useCallback(async () => {
    if (!dados || !apreciacao?.id) {
      setError(t('apreciacaoModais.previewLatex.semDadosRenderizar'));
      return;
    }

    if (!riscos || riscos.length === 0) {
      if (iframeRef.current?.contentDocument) {
        iframeRef.current.contentDocument.open();
        iframeRef.current.contentDocument.write(`
          <div style="padding:40px; font-family:sans-serif; text-align:center; color:#666; min-height:100vh; display:flex; flex-direction:column; align-items:center; justify-content:center;">
            <p style="font-size:20px; font-weight:600; margin-bottom:8px;">${escapeHtml(t('apreciacaoModais.previewLatex.semParteTitulo'))}</p>
            <p style="font-size:14px;">${escapeHtml(t('apreciacaoModais.previewLatex.semParteTexto'))}</p>
          </div>
        `);
        iframeRef.current.contentDocument.close();
      }
      return;
    }

    // Cancelar request anterior se ainda estiver em andamento
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    setIsLoading(true);
    setError(null);

    try {
      const latexContent = gerarLatex(dados, riscos);

      // Usar mode: 'preview' para compilação em passada única (mais rápido)
      const pdfBlob = await apiService.exportarPdf(apreciacao.id, latexContent, {
        mode: 'preview',
        signal: abortController.signal,
      });

      // Se foi cancelado durante a espera, não atualizar estado
      if (abortController.signal.aborted) return;

      pdfBlobRef.current = pdfBlob;

      // Revogar URL anterior
      if (pdfUrlRef.current) {
        window.URL.revokeObjectURL(pdfUrlRef.current);
      }

      const url = window.URL.createObjectURL(pdfBlob);
      pdfUrlRef.current = url;

      if (iframeRef.current) {
        iframeRef.current.src = url + '#page=' + getPaginaAlvo();
      }
    } catch (error: any) {
      // Ignorar erros de cancelamento
      if (error.name === 'AbortError' || abortController.signal.aborted) return;

      console.error('Erro ao renderizar PDF:', error);
      setError(error.message || t('apreciacaoModais.previewLatex.erroRenderizar'));

      if (iframeRef.current?.contentDocument) {
        const latexContent = gerarLatex(dados, riscos);
        iframeRef.current.contentDocument.body.innerHTML = `
          <div style="padding: 20px; font-family: 'Courier New', monospace; white-space: pre-wrap; background: #f5f5f5; min-height: 100vh;">
            <h2 style="color: #d32f2f; margin-bottom: 20px;">${escapeHtml(t('apreciacaoModais.previewLatex.erroRenderizarTitulo'))}</h2>
            <div style="background: #ffebee; padding: 15px; border-radius: 4px; margin-bottom: 20px; border-left: 4px solid #d32f2f;">
              <p style="margin: 0; color: #c62828;"><strong>${escapeHtml(t('apreciacaoModais.previewLatex.erroRotulo'))}</strong> ${escapeHtml(error.message || t('apreciacaoModais.previewLatex.erroDesconhecido'))}</p>
            </div>
            <h3 style="color: #333; margin-bottom: 10px;">${escapeHtml(t('apreciacaoModais.previewLatex.codigoLatex'))}</h3>
            <div style="background: white; padding: 20px; border-radius: 4px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
              <pre style="margin: 0; font-size: 12px; line-height: 1.6; overflow-x: auto;">${escapeHtml(latexContent)}</pre>
            </div>
          </div>
        `;
      }
    } finally {
      if (!abortController.signal.aborted) {
        setIsLoading(false);
      }
    }
  }, [dados, riscos, apreciacao?.id, getPaginaAlvo, t]);

  // Resetar flag de primeira renderização quando o ID da apreciação mudar
  useEffect(() => {
    if (apreciacaoIdRef.current !== apreciacao?.id) {
      apreciacaoIdRef.current = apreciacao?.id;
      isFirstRenderRef.current = true;
    }
  }, [apreciacao?.id]);

  // Debounce adaptativo: primeira renderização imediata, demais com 1.5s de debounce
  useEffect(() => {
    if (dados && apreciacao?.id && isRendered) {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      if (isFirstRenderRef.current) {
        isFirstRenderRef.current = false;
        renderizarPdf();
      } else {
        // Debounce reduzido de 3s para 1.5s (backend agora é mais rápido com cache + single-pass)
        debounceTimeoutRef.current = setTimeout(() => {
          renderizarPdf();
        }, 1500);
      }
    }

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [dados, riscos, apreciacao?.id, isRendered, renderizarPdf]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (pdfUrlRef.current) {
        window.URL.revokeObjectURL(pdfUrlRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);


  const handleRenderizar = () => {
    setIsRendered(true);
    renderizarPdf();
  };

  const gerarPdfAltaQualidade = async (): Promise<Blob> => {
    const { data: riscosImagensHighRes } = await apiService.buscarRiscosImagens(apreciacao.id, 'high');

    const riscosAltaQualidade = riscos.map(r => {
      const hq = riscosImagensHighRes?.[r.id];
      if (!hq) return r;
      return {
        ...r,
        fotoAtualUrl1: hq.fotoAtualUrl1 || r.fotoAtualUrl1,
        fotoAtualUrl2: hq.fotoAtualUrl2 || r.fotoAtualUrl2,
        fotoAtualUrl3: hq.fotoAtualUrl3 || r.fotoAtualUrl3,
        fotoAtualUrl4: hq.fotoAtualUrl4 || r.fotoAtualUrl4,
        fotoPlrUrl: hq.fotoPlrUrl || r.fotoPlrUrl
      };
    });

    const latexContent = gerarLatex(dados, riscosAltaQualidade);
    // Exportação final: usar mode 'final' com 2 passadas para TOC correto
    return await apiService.exportarPdf(apreciacao.id, latexContent, { mode: 'final' });
  };

  // Nome do documento: {Nº ArcelorMittal}_{nº apreciação 4 díg}_{revisão 2 díg}.{ext}
  // Ex.: B0712GAP0149_0001_03.pdf
  const montarNomeDocumento = (extensao: string): string => {
    const numeroAM = String(dados?.numeroArcelorMittal ?? apreciacao?.numeroArcelorMittal ?? '').trim();
    const numeroApr = String(dados?.numero ?? apreciacao?.numero ?? '').trim().padStart(4, '0');
    const revisao = String(dados?.revisao ?? apreciacao?.revisao ?? 0).trim().padStart(2, '0');

    const base = [numeroAM, numeroApr, revisao]
      .filter(Boolean)
      .join('_')
      .replace(/[/\\?%*:|"<>]/g, '-')
      .trim();

    return `${base}.${extensao}`;
  };

  const handleExportarWord = async () => {
    if (!dados || !apreciacao?.id) {
      alert(t('apreciacaoModais.previewLatex.semDadosExportar'));
      return;
    }

    try {
      if (!confirm(t('apreciacaoModais.previewLatex.confirmarWord'))) {
        return;
      }

      setIsLoading(true);

      // Gerar PDF com imagens normais (menor tamanho) mas modo 'final' (2 passadas = sumário + numeração)
      const latexContent = gerarLatex(dados, riscos);
      const pdfFinal = await apiService.exportarPdf(apreciacao.id, latexContent, { mode: 'final' });
      // Identificação do equipamento para a capa reconstruída pelo conversor.
      const { blob: wordBlob, filename } = await apiService.exportarWordViaILovePdf(apreciacao.id, pdfFinal, {
        nome: dados.titulo || dados.nomeMaquina || dados.maquina?.nome || dados.maquina?.descricao,
        area: dados.maquina?.area,
        tag: dados.ativo?.tag,
      });

      const url = window.URL.createObjectURL(wordBlob);
      const link = document.createElement('a');
      link.href = url;
      // O conversor NR12 já devolve o nome montado a partir da capa; ele tem
      // precedência sobre a montagem local (mesmo padrão B0712GAP…_0001_00).
      link.download = filename || montarNomeDocumento('docx');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      alert(t('apreciacaoModais.previewLatex.wordSucesso'));
    } catch (error: any) {
      console.error('Erro ao exportar Word:', error);
      const errorMessage = error.message || t('apreciacaoModais.previewLatex.erroExportarWord');
      alert(t('apreciacaoModais.previewLatex.erroExportarWordDetalhe', { mensagem: errorMessage }));
    } finally {
      setIsLoading(false);
    }
  };


  const handleExportarPdf = async () => {
    if (!pdfBlobRef.current) {
      alert(t('apreciacaoModais.previewLatex.semPdfExportar'));
      return;
    }

    try {
      setIsLoading(true);

      const pdfHQ = await gerarPdfAltaQualidade();

      // Salvar PDF no servidor para o portal do cliente
      try {
        const reader = new FileReader();
        const base64Promise = new Promise<string>((resolve) => {
          reader.onload = () => {
            const base64 = (reader.result as string).split(',')[1];
            resolve(base64);
          };
        });
        reader.readAsDataURL(pdfHQ);
        const pdfBase64 = await base64Promise;
        await apiService.salvarPdfApreciacao(apreciacao.id, pdfBase64);
      } catch (e) {
        console.warn('Falha ao salvar PDF no servidor:', e);
      }

      const url = window.URL.createObjectURL(pdfHQ);
      const link = document.createElement('a');
      link.href = url;
      link.download = montarNomeDocumento('pdf');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('Erro ao exportar PDF em alta qualidade:', error);
      alert(t('apreciacaoModais.previewLatex.erroExportarPdf'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Header */}
      <div className="flex-shrink-0 flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center">
          <FileText className="h-5 w-5 text-blue-500 mr-2" />
          <h2 className="text-lg font-semibold text-gray-900">{t('apreciacaoModais.previewLatex.titulo')}</h2>
          {/* Indicador não-intrusivo de atualização */}
          {isLoading && (
            <span className="ml-3 inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700 animate-pulse">
              <RefreshCw className="h-3 w-3 animate-spin" />
              {t('apreciacaoModais.previewLatex.atualizando')}
            </span>
          )}
        </div>
        <div className="flex items-center space-x-2">
          {/* Seletor de navegação de página */}
          <div className="flex items-center gap-1.5">
            <BookOpen className="h-4 w-4 text-gray-500 flex-shrink-0" />
            <select
              value={tipoNavegacao}
              onChange={(e) => setTipoNavegacao(e.target.value as TipoNavegacao)}
              className="text-xs border border-gray-300 rounded px-2 py-1 bg-white text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500"
              title={t('apreciacaoModais.previewLatex.navegacaoTitle')}
            >
              <option value="primeira">{t('apreciacaoModais.previewLatex.navegacao.primeira')}</option>
              <option value="sumario">{t('apreciacaoModais.previewLatex.navegacao.sumario')}</option>
              <option value="ultima_parte">{t('apreciacaoModais.previewLatex.navegacao.ultimaParte')}</option>
              <option value="ultima">{t('apreciacaoModais.previewLatex.navegacao.ultima')}</option>
            </select>
          </div>
          <button
            onClick={handleExportarPdf}
            className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-red-600 border border-red-700 rounded-md hover:bg-red-700 transition-colors"
            title={t('apreciacaoModais.previewLatex.exportarPdfTitle')}
          >
            <FileDown className="h-4 w-4 mr-1.5" />
            {t('apreciacaoModais.previewLatex.exportarPdf')}
          </button>
          <button
            onClick={handleExportarWord}
            className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            title={t('apreciacaoModais.previewLatex.exportarWordTitle')}
          >
            <Download className="h-4 w-4 mr-1.5" />
            {t('apreciacaoModais.previewLatex.exportarWord')}
          </button>
          <button
            onClick={handleRenderizar}
            className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors"
            title={t('apreciacaoModais.previewLatex.renderizarTitle')}
          >
            <Play className="h-4 w-4 mr-1.5" />
            {t('apreciacaoModais.previewLatex.renderizar')}
          </button>
          {onClose && (
            <button
              onClick={onClose}
              className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-200"
              title={t('apreciacaoModais.previewLatex.fecharTitle')}
            >
              <X className="h-5 w-5" />
            </button>
          )}
        </div>
      </div>

      {/* Content — PDF anterior permanece visível durante atualização */}
      <div className="flex-1 overflow-hidden relative min-h-0">
        {error && !isRendered && (
          <div className="p-4 bg-red-50 border-l-4 border-red-400 text-red-700">
            <p className="font-semibold">{t('apreciacaoModais.previewLatex.erroRotulo')}</p>
            <p>{error}</p>
          </div>
        )}
        <iframe
          ref={iframeRef}
          className="w-full h-full border-0"
          title={t('apreciacaoModais.previewLatex.titulo')}
          style={{ minHeight: 0 }}
        />
      </div>
    </div>
  );
}
